/**
 * Cognito error code → pt-BR copy (AC-9).
 *
 * Two rules the table exists to enforce:
 *
 *  1. **AWS wording never reaches the screen.** Amplify's messages are English and
 *     written for a developer ("Incorrect username or password.").
 *  2. **The copy does not reveal whether the e-mail exists.** `UserNotFoundException`
 *     and `NotAuthorizedException` deliberately share one sentence — an
 *     invitation-only platform (BR-08) that distinguishes them hands an attacker a
 *     user-enumeration oracle for free.
 */

const GENERICA = 'Não foi possível entrar. Tente novamente em instantes.'

const CREDENCIAL_INVALIDA = 'E-mail ou senha incorretos.'

const MENSAGENS: Record<string, string> = {
  // Mesma frase de propósito — ver regra 2 acima.
  NotAuthorizedException: CREDENCIAL_INVALIDA,
  UserNotFoundException: CREDENCIAL_INVALIDA,
  UserNotFoundError: CREDENCIAL_INVALIDA,
  EmptySignInUsername: 'Informe o e-mail.',
  EmptySignInPassword: 'Informe a senha.',

  UserDisabledException: 'Este acesso foi desativado. Fale com o administrador da iOX.',
  UserNotConfirmedException:
    'Este acesso ainda não foi confirmado. Fale com o administrador da iOX.',
  PasswordResetRequiredException:
    'É necessário redefinir a senha deste acesso. Fale com o administrador da iOX.',

  TooManyRequestsException: 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.',
  LimitExceededException: 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.',
  TooManyFailedAttemptsException: 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.',

  NetworkError: 'Sem conexão com o servidor. Verifique a rede e tente de novo.',

  // ── Primeiro acesso ───────────────────────────────────────────────────────────
  // O pool é a autoridade sobre a política de senha; o que a tela valida é cortesia.
  // Quando a política real for mais rígida que a espelhada em `config.json`, é aqui
  // que a recusa aparece.
  InvalidPasswordException:
    'Esta senha não atende à política de segurança. Verifique os requisitos listados.',
  InvalidParameterException:
    'Esta senha não atende à política de segurança. Verifique os requisitos listados.',
  // O desafio vive na memória do adaptador e não sobrevive a um reload.
  SessaoDeDesafioExpirada:
    'A sessão de primeiro acesso expirou. Entre de novo com a senha temporária.',

  // ── Redefinição de senha (self-service, confirmado pelo PO em 14/08) ──────────
  // As duas frases são DIFERENTES de propósito, e aqui distinguir não vaza nada:
  // quem chegou a digitar um código já provou acesso à caixa de e-mail. "Inválido"
  // manda conferir o que foi digitado; "expirou" manda pedir outro. Trocar as duas
  // faz o usuário repetir exatamente o passo que não resolve o problema dele.
  CodeMismatchException: 'Código inválido. Confira o código que enviamos para o seu e-mail.',
  ExpiredCodeException: 'Este código expirou. Volte e solicite um novo.',
  CodeDeliveryFailureException:
    'Não foi possível enviar o código para este e-mail. Fale com o administrador da iOX.',
}

/**
 * Sign-in challenges that have **no screen**.
 *
 * `CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED` used to live here and does not any
 * more: first access is now a real step of `/login` (decided by Fábio, 2026-08-13).
 * What remains are the steps nobody specified and nobody is inventing (§9) — MFA is
 * not configured, `RESET_PASSWORD` waits on the self-service reset decision.
 */
const PROXIMO_PASSO: Record<string, string> = {
  RESET_PASSWORD: 'É necessário redefinir a senha deste acesso. Fale com o administrador da iOX.',
  CONFIRM_SIGN_UP: 'Este acesso ainda não foi confirmado. Fale com o administrador da iOX.',
}

const PREFIXO_PROXIMO_PASSO = 'ProximoPasso:'

export function mensagemDeErroDeLogin(codigo: string): string {
  if (codigo.startsWith(PREFIXO_PROXIMO_PASSO)) {
    const passo = codigo.slice(PREFIXO_PROXIMO_PASSO.length)
    return (
      PROXIMO_PASSO[passo] ??
      'Este acesso exige uma etapa adicional que ainda não está disponível. Fale com o administrador da iOX.'
    )
  }
  return MENSAGENS[codigo] ?? GENERICA
}

/**
 * O mesmo código, outra tela, outro significado — pedido de código de redefinição.
 *
 * ⚠️ O caso que obriga esta função a existir: uma conta convidada que ainda está em
 * `FORCE_CHANGE_PASSWORD` **não pode** usar "esqueci minha senha". O pool recusa com
 * `NotAuthorizedException` ("User password cannot be reset in the current state"), e o
 * adaptador só preserva o NOME da exceção — que na tela de credenciais significa
 * "e-mail ou senha incorretos". Renderizar aquela frase aqui mandaria o usuário
 * conferir uma senha que ele nem chegou a digitar.
 *
 * 🔒 A frase é CONDICIONAL de propósito. Este erro só acontece para uma conta que
 * existe, então uma mensagem afirmativa ("sua conta ainda não concluiu o primeiro
 * acesso") confirmaria a existência do e-mail e devolveria o oráculo de enumeração que
 * `prevent_user_existence_errors` e a frase compartilhada do login fecham (BR-08).
 * Ela cobre as duas hipóteses sem dizer qual delas é a verdadeira.
 */
export function mensagemDeErroDeRedefinicao(codigo: string): string {
  if (codigo === 'NotAuthorizedException') {
    return (
      'Não foi possível iniciar a redefinição para este e-mail. Se você recebeu um convite e ' +
      'ainda não concluiu o primeiro acesso, entre com a senha temporária que enviamos. Caso ' +
      'contrário, fale com o administrador da iOX.'
    )
  }
  return mensagemDeErroDeLogin(codigo)
}

/** Shown on `/login` when the previous session died mid-use (AC-5). */
export const MENSAGEM_SESSAO_EXPIRADA =
  'Sua sessão expirou por inatividade. Entre novamente para continuar.'
